import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";

// Przybornik nagłówków i stopek graficznych dokumentów MS Word (port „Przybornik stopek"
// z ustawień dokumentów Magistratu). Zestaw = nazwa + 4 grafiki: nagłówek i stopka
// pierwszej strony oraz stron kolejnych. Zestawy są per firma; definiuje je zakładka
// ustawień (DocumentFootersTab), wstawia dodatek Worda (Elementy dokumentu → Nagłówek
// i stopka), który czyta te same endpointy.

export const documentFootersQueryKeys = {
  list: "documentFooters.list",
  image: "documentFooters.image",
};

/** Nazwy slotów są jednocześnie segmentem adresu API (PascalCase, jak w BE). */
export type TDocumentFooterSlot =
  | "FirstPageHeader"
  | "PageHeader"
  | "FirstPageFooter"
  | "PageFooter";

export interface IDocumentFooterGroup {
  Id: string;
  Name: string;
  HasFirstPageHeader: boolean;
  HasPageHeader: boolean;
  HasFirstPageFooter: boolean;
  HasPageFooter: boolean;
  CreationDate: string;
  LastModified: string;
}

export interface IDocumentFooterGroupsResponse {
  Items: IDocumentFooterGroup[];
  /** Właściciel firmy albo rola z workspace.edit. */
  CanEdit: boolean;
}

export interface ISaveDocumentFooterGroupRequest {
  Id?: string | null;
  Name: string;
}

const getList = (): Promise<{ data: IDocumentFooterGroupsResponse }> =>
  axiosInstance.get("settings/footers");

const useGetList = () =>
  useCustomQuery(documentFootersQueryKeys.list, () => getList());

const save = (
  payload: ISaveDocumentFooterGroupRequest,
): Promise<{ data: IDocumentFooterGroup }> =>
  axiosInstance.post("settings/footers", payload);

const useSave = () => useCustomMutation(save);

const remove = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`settings/footers/${id}`);

const useRemove = () => useCustomMutation(remove);

const getImage = (id: string, slot: TDocumentFooterSlot): Promise<{ data: Blob }> =>
  axiosInstance.get(`settings/footers/${id}/image/${slot}`, {
    responseType: "blob",
  });

/** Podgląd grafiki slotu; pusty slot to 404, więc zapytanie nie ponawia. */
const useGetImage = (
  id: string | null,
  slot: TDocumentFooterSlot,
  enabled: boolean,
  version?: string,
) =>
  useCustomQuery(
    [documentFootersQueryKeys.image, id, slot, version],
    () => getImage(id as string, slot),
    { enabled: !!id && enabled, retry: false, staleTime: Infinity },
  );

const uploadImage = (payload: {
  id: string;
  slot: TDocumentFooterSlot;
  file: File;
}): Promise<{ data: IDocumentFooterGroup }> => {
  const formData = new FormData();
  formData.append("file", payload.file);

  return axiosInstance.post(
    `settings/footers/${payload.id}/image/${payload.slot}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
};

const useUploadImage = () => useCustomMutation(uploadImage);

const removeImage = (payload: {
  id: string;
  slot: TDocumentFooterSlot;
}): Promise<{ data: IDocumentFooterGroup }> =>
  axiosInstance.delete(`settings/footers/${payload.id}/image/${payload.slot}`);

const useRemoveImage = () => useCustomMutation(removeImage);

const documentFootersService = {
  getList,
  useGetList,
  save,
  useSave,
  remove,
  useRemove,
  getImage,
  useGetImage,
  uploadImage,
  useUploadImage,
  removeImage,
  useRemoveImage,
};

export default documentFootersService;
